import React, { FC, useEffect, useState } from "react";
import { getDomain } from "@jasper/shared";
import { useParams } from "react-router-dom";
import { Box, MantineProvider, Text } from "@mantine/core";
import { JasperWordmark } from "@jasper/forms";
import { notifications } from "@mantine/notifications";
import Cookies from "js-cookie";
import { IconTrash } from "@tabler/icons-react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { FileWithPath } from "@mantine/dropzone";
import axios from "axios";
import {
  handleImageUpload,
  Carousel,
  ButtonFileInput,
  CreateOrderHelpFileDocument,
  DeleteOrderHelpFileDocument,
  GET_FILE_DOWNLOAD_URL,
  getPresignedPost,
  GetOrderHelpFilesDocument,
  isUUID,
} from "@jasper/shared";

interface RouteParams {
  token: string | undefined;
  id: string | undefined;
  name: string | undefined;
  filetype: string | undefined;
}

export const UploadFileHelpPage: FC = () => {
  const [filesToShow, setFilesToShow] = useState<any[]>([]);
  const param = useParams<keyof RouteParams>();

  // Set authentication cookie
  if (param.token !== undefined) {
    Cookies.set("Authentication", param.token, {
      expires: 1,
      domain: getDomain(import.meta.env.MODE),
    });
  }

  if (!param.id || !param.name || !param.token) {
    return null; // Handle missing params gracefully
  }

  const [createOrderHelpFile] = useMutation(CreateOrderHelpFileDocument);
  const [deleteOrderHelpFile] = useMutation(DeleteOrderHelpFileDocument);
  const [getPresignedPostDocument] = useLazyQuery(getPresignedPost);
  const [getPresignedDownload] = useLazyQuery(GET_FILE_DOWNLOAD_URL);

  const { data, refetch } = useQuery(GetOrderHelpFilesDocument, {
    variables: { orderId: param.id },
  });

  const files = data?.getOrderHelpFiles ?? [];

  const getFileUrl = async (key: string) => {
    const res = await getPresignedDownload({ variables: { key } });
    return res?.data?.getPresignedFileDownloadUrl;
  };

  const uploadFileToS3 = async (
    files: FileWithPath[]
  ): Promise<string[] | null> => {
    if (!files) return null;
    const filesWithKey: string[] = await Promise.all(
      files.map(async file => {
        const getPresignedPostData = await getPresignedPostDocument({
          variables: {
            fileName: file.name,
          },
        });
        const presignedPost = JSON.parse(
          getPresignedPostData?.data?.getPresignedFileUploadUrl ?? ""
        );
        const uploadFormData = new FormData();
        Object.keys(presignedPost.fields).forEach(key => {
          uploadFormData.append(key, presignedPost.fields[key]);
        });
        uploadFormData.append("file", file);

        const res = await axios.post(presignedPost.url, uploadFormData, {
          // investiguer ici pour erreur S3
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (res.status === 201 && presignedPost.fields.key) {
          return presignedPost.fields.key;
        }
      })
    );
    return filesWithKey.filter(key => key);
  };

  const deleteOneHelpFile = async (fileId: string) => {
    await deleteOrderHelpFile({ variables: { orderId: param.id, fileId } });
    await refetch();
  };

  const openFileInNewTab = async (key: string) => {
    const newTab = window.open("", "_blank");
    const url = await getFileUrl(key);

    if (newTab && url) {
      newTab.location.href = url;
    }
  };

  useEffect(() => {
    const resolvePreviews = async () => {
      setFilesToShow(
        await Promise.all(
          files.map(async (file: any) => ({
            ...file,
            path: await getFileUrl(file.key),
          }))
        )
      );
    };

    resolvePreviews();
  }, [data]);

  return (
    <>
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          backgroundColor: "#5172E6",
          padding: "20px 0",
        }}
      >
        <div
          style={{
            width: "80%",
            minHeight: "60%",
            margin: "auto",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "white",
            borderRadius: "10px",
            padding: "20px",
          }}
        >
          <JasperWordmark size={50} style={{ margin: "20px auto" }} />
          <p>
            Complétez votre message d&apos;aide avec une ou plusieurs photos.
            <br />
          </p>
          <p>Nom du patient : {param.name}</p>
          <ButtonFileInput
            onFilesAdd={async uploadedFile => {
              const fileList: File[] = await Promise.all(
                [...uploadedFile].map(async file => {
                  const name = file.name.split(".");
                  return new File(
                    [(await handleImageUpload(file)) ?? file],
                    `photo_${files.length + (length + 1)}.${name[name.length - 1]}`,
                    {
                      type: file.type,
                    }
                  );
                })
              );
              try {
                const keys = await uploadFileToS3(fileList);
                if (!keys?.length) return;

                await Promise.all(
                  keys.map(key =>
                    createOrderHelpFile({
                      variables: { orderId: param.id, key },
                    })
                  )
                );
                await refetch();
              } catch {
                notifications.show({
                  title: "Les fichiers n'ont pas pu être partagés",
                  color: "red",
                  message:
                    "Vérifiez votre connexion puis ajoutez à nouveau un fichier",
                });
              }
            }}
            buttonText={
              <span>
                Prendre une photo ou
                <br />
                choisir dans la photothèque
              </span>
            }
            accept="image/*"
          />
          {filesToShow.length > 0 && (
            <Box>
              <div
                style={{
                  padding: "20px",
                }}
              >
                <Carousel imageList={[...filesToShow].reverse()} />
              </div>
              <MantineProvider>
                <Text>Fichiers ajoutés:</Text>
                <Box>
                  {[...filesToShow].reverse().map(file => (
                    <Box
                      key={file.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      {file.url && (
                        <a
                          id={file.key}
                          style={{
                            display: "none",
                          }}
                          href={file.url}
                          download={file.key}
                          target="_blank"
                          rel="noreferrer"
                        ></a>
                      )}
                      <div
                        onClick={async () => {
                          if (file.url) {
                            document.getElementById(file.key)?.click();
                          } else {
                            openFileInNewTab(file.key);
                          }
                        }}
                        className="link-style"
                      >
                        {file.path && (
                          <div className="icon-style">
                            <img src={file.path} />
                          </div>
                        )}
                        <div
                          style={{
                            display: "flex",
                          }}
                        >
                          <span
                            style={{
                              margin: "auto",
                            }}
                          >
                            {isUUID(file.key)
                              ? file.key.substring(37)
                              : file.key}
                          </span>
                        </div>
                      </div>
                      <Box
                        style={{
                          cursor: "pointer",
                          display: "flex",
                        }}
                      >
                        <IconTrash
                          style={{
                            margin: "auto",
                          }}
                          onClick={async () => {
                            await deleteOneHelpFile(file.id);
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </MantineProvider>
            </Box>
          )}
        </div>
      </div>
    </>
  );
};
